import React from 'react';
import { useParams, Redirect } from 'react-router-dom';
import { graphql } from 'babel-plugin-relay/macro';
import { useFragment } from 'react-relay';
import { Container, Row, Col } from 'react-bootstrap';
import SpeakerDescription from '../../components/SpeakerDescription';
import LinkButton from '../../uikit/Links/LinkButton';
import AgendaItem from '../../components/AgendaItem';
import Person from '../../uikit/Cards/Person';
import './SpeakerPage.sass';

import type { SpeakerPageQuery$key, SpeakerPageQuery } from './__generated__/SpeakerPageQuery.graphql';
import { ROUTER_TYPES } from "../../utils/constants";
import Breadcrumns from "../../uikit/Breadcrumbs/Breadcrumbs";

interface Props {
  query: SpeakerPageQuery$key;
}

function SpeakerPage(props: Props) {
  const { currentSpeakerUrl } = useParams<{ currentSpeakerUrl?: string }>();

  const data: SpeakerPageQuery = useFragment(
    graphql`
			fragment SpeakerPageQuery on PublishedContentByTypeQuery {
				speakers {
					items {
						... on SpeakersPublishedContent {
							_children {
								items {
									... on SpeakerPublishedContent {
										_id
										_url
										pageTitle
										bio
										speakerName
										speakerPreviewDescription
										speakerImage
										location {
											... on LocationPublishedContent {
												city
												countryName
												country {
													... on FilePublishedMedia {
														_url
													}
												}
											}
										}
										facebookLink
										twitterLink
										linkedinLink
									}
								}
							}
						}
					}
				}
				speech {
					items {
						... on SpeechPublishedContent {
							_id
							duration
							language
							startTime
							text
							title
							track
							type
							speakers {
								... on SpeakerPublishedContent {
									speakerName
									speakerImage
									speakerPreviewDescription
									_url
									location {
										... on LocationPublishedContent {
											country {
												... on FilePublishedMedia {
													_url
												}
											}
										}
									}
								}
							}
							_parent {
								... on EventDayPublishedContent {
									_id
									title
									date
									endDayTime
								}
							}
						}
					}
				}
			}
		`,
    props.query
  );

  const currentSpeaker = data?.speakers?.items?.[0]?._children?.items?.find(
    (i) => currentSpeakerUrl && i?._url?.includes(currentSpeakerUrl)
  );

  const agendaData = data?.speech?.items;
  const currentAgendaData = agendaData?.filter((i) =>
    i?.speakers?.some(
      (speaker) => speaker?.speakerName === currentSpeaker?.speakerName
    )
  );

  if (!currentSpeaker) {
    return <Redirect to="/404"/>;
  }

  const { speakerName } = currentSpeaker || {};

  return (
    <div className="speaker-page">
      <Container>
        {speakerName && (
          <Row>
            <Col xs={12}>
              <Breadcrumns
                parentLabel="speakers"
                parentLink={ROUTER_TYPES.speakers}
                currentPage={speakerName}
              />
            </Col>
          </Row>)}
        <Row className="speaker-page__content">
          <Col xs={12} lg={9} className="speaker-page__info">
            <SpeakerDescription currentSpeaker={currentSpeaker}/>

            <div className="speaker-page__agenda speaker-page-agenda">
              {currentAgendaData?.length === 0 ? null : (
                <h2 className="uikit-label-small-caps">Speakers Program</h2>
              )}
              {currentAgendaData?.length === 0 ? null : (
                <div
                  className="speaker-page-agenda__list agenda-list-speaker"
                >
                  {currentAgendaData?.map((agenda: any) => (
                    <AgendaItem key={agenda.startTime} currentAgendaData={agenda}/>
                  ))}
                </div>
              )}

              <div className="speaker-page-agenda__button">
                <LinkButton
                  label="See the Agenda"
                  isExternal={false}
                  hrefSource="/agenda"
                />
              </div>
            </div>
          </Col>
          <Col xs={12} lg={3} className="speaker-page__person">
            <Person data={currentSpeaker}/>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default SpeakerPage;
